import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Input from '@amzn/awsui-components-console/input';
import Spinner from '@amzn/awsui-components-console/spinner';
import TruncatedText from '@amzn/awsui-components-console/truncated-text';

import CompactPagination from 'src/common/ui/CompactPagination';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';

import { useDebouncedValue } from 'src/common/hooks/useDebouncedValue';
import { validateSearchPrefix } from 'src/common/utils/searchValidation';
import { buildNamespacePath } from 'src/common/routes';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import { useNamespaceList } from './useNamespaceList';
import styles from './NamespaceSideNav.module.scss';

const SEARCH_DEBOUNCE_MS = 600;

/**
 * Minimum characters before the prefix filter is sent to the server. Typing
 * 1-2 characters would match a large fraction of namespaces, so the request is
 * withheld until the input is selective enough to be worth a round-trip.
 */
const SEARCH_MIN_CHARS = 3;

interface NamespaceSideNavProps {
  activeNamespace?: string;
}

const NamespaceSideNav: React.FC<NamespaceSideNavProps> = ({
  activeNamespace,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const debouncedFilter = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);
  const navigate = useNavigate();

  // Below the threshold the filter is dropped entirely, so the sidebar keeps
  // browsing the unfiltered list rather than querying on a 1-2 char prefix.
  // An invalid prefix is dropped for the same reason: the server would reject
  // it with a ValidationException, so the request is never issued.
  const trimmedFilter = debouncedFilter.trim();
  const effectiveFilter =
    trimmedFilter.length >= SEARCH_MIN_CHARS &&
    validateSearchPrefix(t, trimmedFilter) === null
      ? trimmedFilter
      : '';

  // Hints are driven by the immediate input (not the debounced value) so they
  // appear as the user types rather than 600ms later. A constraint violation
  // takes precedence over the min-length hint: it is the more specific problem,
  // and a too-short value is also usually still being typed.
  const trimmedInput = searchValue.trim();
  const searchError = validateSearchPrefix(t, trimmedInput);
  const showMinCharsHint =
    searchError === null &&
    trimmedInput.length > 0 &&
    trimmedInput.length < SEARCH_MIN_CHARS;

  const {
    namespaceIds,
    currentPage,
    pagesCount,
    hasMore,
    loading,
    isLoadingMore,
    error,
    totalLoaded,
    goToPage,
  } = useNamespaceList(effectiveFilter);

  return (
    <div className={styles.sidebar}>
      <div className={styles.content}>
        {/* Search */}
        <Box padding={{ horizontal: 'm', bottom: 's' }} margin={{ top: 'm' }}>
          <Input
            type="search"
            value={searchValue}
            placeholder={t('nav-find-namespace')}
            ariaLabel={t('nav-find-namespace')}
            onChange={({ detail }) => setSearchValue(detail.value)}
          />
          {searchError !== null && (
            <Box variant="small" color="text-status-error">
              {searchError}
            </Box>
          )}
          {showMinCharsHint && (
            <Box variant="small" color="text-status-error">
              {t('nav-min-chars-hint', { min: SEARCH_MIN_CHARS })}
            </Box>
          )}
        </Box>

        {/* Namespace list — full rendering control */}
        <nav aria-label={t('nav-namespace-nav-aria')}>
          {namespaceIds.map((ns) => {
            const isActive = ns === activeNamespace;
            const href = buildNamespacePath(ns);
            return (
              <a
                key={ns}
                href={href}
                className={`${styles.navItem}${isActive ? ` ${styles.navItemActive}` : ''}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  navigate(href);
                }}
              >
                <Box
                  padding={{ vertical: 'xxxs', horizontal: 's' }}
                  fontSize="body-m"
                >
                  <div className={styles.navItemRow}>
                    <span className={styles.navItemText}>
                      <TruncatedText tooltipText={ns}>{ns}</TruncatedText>
                    </span>
                  </div>
                </Box>
              </a>
            );
          })}
        </nav>

        {loading && (
          <Box textAlign="center" padding={{ vertical: 's' }}>
            <Spinner />
          </Box>
        )}

        {error && (
          <Box padding={{ horizontal: 's', vertical: 's' }}>
            {/*
              A refusal shows the service's own wording; anything else shows the
              header alone. `error.message` is deliberately not echoed for the
              generic case: this is a ~200px rail, and an Apollo message wraps
              into an unreadable block that pushes the pager off-screen. The
              console keeps the detail.
            */}
            {isAccessDenied(error) ? (
              <AccessDeniedAlert
                message={getAccessDeniedMessage(error)}
                deniedNamespace={getAccessDeniedNamespace(error)}
                data-testid="namespace-nav-access-denied"
              />
            ) : (
              <Alert type="error" header={t('nav-load-failed-header')}>
                {t('nav-load-failed-body')}
              </Alert>
            )}
          </Box>
        )}

        {!loading && !error && totalLoaded === 0 && (
          <Box
            padding={{ horizontal: 's', vertical: 's' }}
            variant="small"
            color="text-body-secondary"
          >
            {effectiveFilter ? t('nav-no-matching') : t('nav-no-namespaces')}
          </Box>
        )}
      </div>

      {/* Pagination — pinned to bottom */}
      <div className={styles.footer}>
        {/* Tight horizontal padding: the pager needs the full sidebar width to
            fit its 9 controls at NAV_WIDTH_MIN. */}
        <Box padding={{ horizontal: 'xs', vertical: 'xs' }}>
          <CompactPagination
            currentPageIndex={currentPage}
            pagesCount={pagesCount}
            hasMore={hasMore}
            disabled={loading || isLoadingMore}
            onPageChange={goToPage}
          />
        </Box>
      </div>
    </div>
  );
};

export default NamespaceSideNav;
